/**
 * Assessment of one candidate against one mandate.
 *
 * Layout from SAKETH-KB/DESIGN/AI Sourcing Analysis v4.html: a narrow panel
 * while loading that widens once the analysis lands into two cards over a
 * sideways strip of panels - notes, assessment, questions, external news -
 * which the user can reorder by dragging a panel's title.
 *
 * Two requests, fired together:
 *   analyse_candidate  the score, strengths, weaknesses, questions
 *   external_check     web news on the person and their current employer -
 *                      slower, separately fallible, never part of the score
 *
 * Departures from the design file, each because it could not be literally
 * right with real data:
 *   - no "Sample results" badge; these are real results
 *   - the employer group names the current employer only, because that is
 *     all the search covers; the design says "current & past employers"
 *   - the lookback reads from the server (3 years), not the design's 24 months
 *   - the External verdict is written by code from the items that survived
 *     the checks, not by the model - a model-written verdict could cite an
 *     item that was thrown away, and "no adverse media" must not be a guess
 *   - person items carry "Possible match on …", the guard against attaching a
 *     namesake's news to this candidate
 *   - z-index 1050 and click-outside-to-close, matching PINE's other panels
 *   - notes live only while the window is open, and say so; the design's
 *     "Autosaves" would be a promise this screen does not keep yet
 *   - the score ring keeps PINE's colour bands, and the timeline keeps bars
 *     built from real dates
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import toastr from "../../../utils/toast";

const ORANGE = "#e8692a";
const TEXT = "#212529";
const MUTED = "#6c757d";
const STEP_MS = 1100;

const STEPS = [
  "Reading the mandate and its key attributes...",
  "Reading career history, notes and profile...",
  "Searching the web for news on this person and their employers...",
  "Testing this person against the mandate...",
  "Checking every quoted claim against its source...",
];

/** When in the process each group of questions is meant to be asked. */
const QUESTION_STAGES = [
  { stage: "briefing", label: "At briefing" },
  { stage: "assessment", label: "At assessment" },
];

/** Badge colours per tone, from the design file: [text, background]. */
const TONES = {
  Positive: ["#1e7b34", "#e6f4ea"],
  Negative: ["#b02a37", "#fbe9eb"],
  Neutral: ["#495057", "#eceef0"],
};

const toneOf = (sentiment) =>
  sentiment === "positive"
    ? "Positive"
    : sentiment === "negative"
      ? "Negative"
      : "Neutral";

/** "www.reuters.com/business/..." -> "reuters.com", for the source line. */
const domainOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch (e) {
    return "source";
  }
};

const KIND_LABEL = {
  layoffs: "Layoffs",
  financial: "Financial",
  leadership: "Leadership",
  legal: "Legal",
  growth: "Growth",
  other: "News",
};

/** Words that carry no identity in a company name, skipped when shortening. */
const NAME_FILLER = new Set([
  "of", "and", "the", "for", "&", "-", "ltd", "ltd.", "limited", "pvt", "pvt.",
  "private", "inc", "inc.", "llc", "plc", "co", "co.", "company",
]);

/**
 * A company name short enough to sit under a narrow bar. Short names are left
 * alone; long ones become their initials ("Cultural Development Fund" -> CDF).
 * The full name is always on hover.
 */
function shortCompany(name) {
  if (!name) return "";
  const clean = String(name).trim();
  if (clean.length <= 14) return clean;
  const initials = clean
    .split(/\s+/)
    .filter((w) => !NAME_FILLER.has(w.toLowerCase()))
    .map((w) => w[0])
    .filter((c) => /[A-Za-z0-9]/.test(c))
    .join("")
    .toUpperCase();
  return initials.length >= 2 ? initials : `${clean.slice(0, 12)}…`;
}

/**
 * Colour carries one meaning only: the role they are still in. Width already
 * says how long, position already says when, and a shade that darkened over
 * time read as "more senior" or "better" - which it never measured.
 */
const TIMELINE_PAST = { background: "#f6d3b9", color: "#8a4416" };
const TIMELINE_CURRENT = { background: ORANGE, color: "#fff" };

const fmtYears = (y) => `${Number(y).toFixed(1).replace(/\.0$/, "")}y`;

/**
 * Career timeline - built by the server from candidate_experiences dates.
 * Bar widths are the roles' real lengths; nothing here comes from the model.
 */
/** Bar length per year of tenure, and the narrowest a bar may be - wide
 *  enough that a title is still a word, not three letters and an ellipsis. */
const PX_PER_YEAR = 60;
const MIN_ROLE_PX = 120;

function TimelineCard({ timeline }) {
  const roles = timeline?.roles || [];
  const strip = useRef(null);

  // Open on the right-hand end: the current role is what a consultant looks
  // for first, and the early career is a scroll to the left.
  useEffect(() => {
    if (strip.current) strip.current.scrollLeft = strip.current.scrollWidth;
  }, [roles.length]);

  if (!roles.length) return null;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e3e3e3",
        borderRadius: "4px",
        padding: "18px 20px 12px",
        fontSize: "13px",
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: "12px",
          marginBottom: "12px",
        }}
      >
        <span style={{ fontSize: "14px", fontWeight: 500 }}>Career timeline</span>
        <span
          style={{
            fontSize: "12px",
            color: MUTED,
            display: "flex",
            alignItems: "center",
            gap: "6px",
            marginLeft: "auto",
          }}
        >
          {roles.some((r) => r.current) && (
            <>
              <span
                style={{
                  width: "9px",
                  height: "9px",
                  borderRadius: "2px",
                  background: ORANGE,
                  display: "inline-block",
                }}
              />
              current role ·
            </>
          )}
        </span>
        <span style={{ fontSize: "12px", color: MUTED }}>
          {timeline.avg_tenure_years != null && (
            // Tenure bands: under 2.5 years red, 2.5 to 3 amber, over 3 green.
            // Darker shades than the dial's, because this is small text.
            <span
              title="Average time in a finished role. Under 2.5 years red, 2.5-3 amber, over 3 green."
              style={{
                fontWeight: 500,
                color:
                  timeline.avg_tenure_years > 3
                    ? "#1e7b34"
                    : timeline.avg_tenure_years >= 2.5
                      ? "#a86f00"
                      : "#b02a37",
              }}
            >
              avg. tenure {timeline.avg_tenure_years} yrs
            </span>
          )}
          {` · ${roles.length} ${roles.length === 1 ? "role" : "roles"}`}
        </span>
      </div>

      {/* Scrolls sideways when the career is longer than the card. Each bar
          grows from its own length, so a short history still fills the card
          and a long one keeps its proportions instead of being squeezed. */}
      <div
        ref={strip}
        className="pine-sa-scroll"
        style={{ display: "flex", gap: "3px", overflowX: "auto", paddingBottom: "6px" }}
      >
        {roles.map((r, i) => (
          <div
            key={i}
            title={[
              r.title,
              r.company,
              `${r.start_year}${r.current ? " - now" : ""}`,
              fmtYears(r.years),
            ]
              .filter(Boolean)
              .join(" · ")}
            style={{
              flex: `${r.years} 0 ${Math.max(MIN_ROLE_PX, r.years * PX_PER_YEAR)}px`,
              minWidth: 0,
            }}
          >
            <div
              style={{
                height: "26px",
                borderRadius: "3px",
                ...(r.current ? TIMELINE_CURRENT : TIMELINE_PAST),
                fontSize: "11px",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {fmtYears(r.years)}
            </div>
            <div
              style={{
                marginTop: "8px",
                fontSize: "12px",
                color: TEXT,
                lineHeight: 1.4,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {r.title || "Role not named"}
            </div>
            <div
              style={{
                fontSize: "11px",
                color: MUTED,
                lineHeight: 1.4,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {[shortCompany(r.company), r.start_year].filter(Boolean).join(" · ")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Pill colours per priority: [text, background, border]. */
const PRIORITY_STYLE = {
  HIGH: ["#1e7b34", "#e6f4ea", "#b7dfc3"],
  MEDIUM: ["#c95518", "#fff3ec", "#f6c7a8"],
  LOW: ["#495057", "#eceef0", "#d6d9dc"],
};

/**
 * The priority, read by code from the start of the recommendation - the
 * prompt requires it to begin with HIGH, MEDIUM or LOW PRIORITY. Not asked of
 * the model a second time, so the pill and the recommendation cannot disagree.
 */
const priorityOf = (recommendation) => {
  const m = /^\s*(HIGH|MEDIUM|LOW)\s+PRIORITY/i.exec(recommendation || "");
  return m ? m[1].toUpperCase() : null;
};

/**
 * The one line beside the score. The model's headline when it gave one;
 * otherwise the recommendation's first sentence with the label taken off.
 */
const headlineOf = (a) => {
  if (a?.headline) return a.headline;
  const rest = String(a?.recommendation || "")
    .replace(/^\s*(HIGH|MEDIUM|LOW)\s+PRIORITY\s*[-:—,.]*\s*/i, "")
    .trim();
  const first = rest.split(/(?<=[.!?])\s/)[0] || "";
  return first.length > 120 ? `${first.slice(0, 117)}…` : first;
};

/** Score ring, priority pill, one-line next step. */
function ScoreCard({ analysis }) {
  const score = Math.max(0, Math.min(100, Number(analysis?.score) || 0));
  const priority = priorityOf(analysis?.recommendation);
  const [fg, bg, border] = PRIORITY_STYLE[priority] || PRIORITY_STYLE.LOW;

  // Dial colour by band: above 75 green, above 50 amber, 50 and below orange.
  // Amber rather than pure yellow - yellow on white cannot be seen.
  const dial = score > 75 ? "#28a745" : score > 50 ? "#e0a800" : ORANGE;

  const R = 40;
  const C = 2 * Math.PI * R;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e3e3e3",
        borderRadius: "4px",
        padding: "18px 26px",
        display: "flex",
        alignItems: "center",
        gap: "26px",
        fontSize: "14px",
        minWidth: 0,
      }}
    >
      <div
        style={{ position: "relative", width: "92px", height: "92px", flex: "none" }}
        title="0-100: fit to this mandate, capped by how much PINE actually knows about the person"
      >
        <svg width="92" height="92" viewBox="0 0 92 92">
          <circle cx="46" cy="46" r={R} fill="none" stroke="#eeeeee" strokeWidth="7" />
          <circle
            cx="46"
            cy="46"
            r={R}
            fill="none"
            stroke={dial}
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={`${(score / 100) * C} ${C}`}
            transform="rotate(-90 46 46)"
          />
        </svg>
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
          }}
        >
          <span style={{ fontSize: "30px", fontWeight: 500, color: TEXT }}>{score}</span>
          <span style={{ fontSize: "11px", color: MUTED, marginTop: "2px" }}>/100</span>
        </div>
      </div>

      <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "8px" }}>
        <span style={{ color: MUTED }}>Sourcing score</span>
        {priority && (
          <span
            style={{
              alignSelf: "flex-start",
              fontSize: "12px",
              fontWeight: 700,
              letterSpacing: "0.02em",
              color: fg,
              background: bg,
              border: `1px solid ${border}`,
              borderRadius: "12px",
              padding: "2px 10px",
            }}
          >
            {priority} PRIORITY
          </span>
        )}
        <span style={{ color: TEXT, lineHeight: 1.5, textWrap: "pretty" }}>
          {headlineOf(analysis)}
        </span>
        {analysis?.basis && (
          <span style={{ fontSize: "12px", color: MUTED, lineHeight: 1.4 }}>
            Based on: {analysis.basis}
          </span>
        )}
      </div>
    </div>
  );
}

const SECTION = {
  position: "relative",
  overflow: "auto",
  background: "#f7f7f7",
  border: "1px solid #ececec",
  borderRadius: "4px",
  padding: "20px",
  display: "flex",
  flexDirection: "column",
  fontSize: "14px",
  lineHeight: 1.8,
  minWidth: 0,
  minHeight: 0,
  transition: "box-shadow .25s",
};

// About two and a half panels in view; the strip snaps one panel at a time.
const PANEL = { flex: "0 0 clamp(360px, 42%, 620px)", scrollSnapAlign: "start" };
const STRIP_GAP = 18;
// Full-screen panels: two to a screen.
const PANEL_HALF = { flex: `0 0 calc(50% - ${STRIP_GAP / 2}px)` };

// The panels in their default order. The user's own order is a per-viewer
// preference, so it lives in the browser rather than in PINE.
// The order panels open in until someone drags them.
const PANEL_IDS = ["strengths", "questions", "notes", "external"];
// A saved question keeps its note across re-runs by its row id; an unsaved
// one falls back to its place in the list.
const noteKey = (q, stage, i) => (q.id ? `q${q.id}` : `${stage}-${i}`);
// The server refuses longer; the panel says so before sending.
const MAX_NOTE_CHARS = 20000;
// The recording upload's own limit (candidates.routes.js).
const MAX_RECORDING_BYTES = 500 * 1024 * 1024;
// The same cap the Edit Status form puts on a resume.
const MAX_CV_BYTES = 5 * 1024 * 1024;
// Small text buttons under a question: Add answer, Edit, Cancel.
const LINK_BUTTON = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  border: "none",
  background: "none",
  padding: 0,
  fontFamily: "inherit",
  fontSize: "13px",
  fontWeight: 500,
  color: ORANGE,
  cursor: "pointer",
  textAlign: "left",
};
// v2 when the default order changed, so orders dragged while testing the old
// default do not hide the new one.
const ORDER_KEY = "pine-sa-panel-order-v2";
const readOrder = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(ORDER_KEY));
    if (
      Array.isArray(saved) &&
      saved.length === PANEL_IDS.length &&
      PANEL_IDS.every((id) => saved.includes(id))
    ) {
      return saved;
    }
  } catch (e) {
    // Storage can be blocked; the default order is fine.
  }
  return PANEL_IDS;
};

// A panel's title is its drag handle.
const HANDLE = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontWeight: 700,
  cursor: "grab",
  touchAction: "none",
  userSelect: "none",
};

// Full page and close, top right.
const HEADER_BUTTON = {
  width: "24px",
  height: "24px",
  flex: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: `1px solid ${ORANGE}`,
  borderRadius: "4px",
  background: "#fff",
  cursor: "pointer",
  padding: 0,
  color: TEXT,
};

const svg =(size, children, stroke, width = 2) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={stroke || "currentColor"}
    strokeWidth={width}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const IconRobot = () =>
  svg(
    22,
    <>
      <path d="M12 8V4H8" />
      <rect width="16" height="12" x="4" y="8" rx="2" />
      <path d="M2 14h2" />
      <path d="M20 14h2" />
      <path d="M15 13v2" />
      <path d="M9 13v2" />
    </>,
    ORANGE,
  );

const IconGrip = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="#adb5bd" style={{ flex: "none" }}>
    {[5, 12, 19].map((y) => (
      <g key={y}>
        <circle cx="9" cy={y} r="1.6" />
        <circle cx="15" cy={y} r="1.6" />
      </g>
    ))}
  </svg>
);

const IconPencil = () =>
  svg(
    13,
    <>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </>,
  );

const IconCopy = () =>
  svg(
    15,
    <>
      <rect width="14" height="14" x="8" y="8" rx="2" />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </>,
  );

/** Where each panel sits now, so a reorder can slide them from there. */
function panelPositions(row, skip) {
  const at = {};
  row.querySelectorAll("[data-panel]").forEach((n) => {
    if (n.dataset.panel === skip) return;
    at[n.dataset.panel] = n.getBoundingClientRect().left;
    (n.getAnimations ? n.getAnimations() : []).forEach((anim) => anim.cancel());
  });
  return at;
}

/** Keeps the dragged panel under the pointer, wherever a reorder put it. */
function followPointer(d, row) {
  const dx = d.x - d.x0 + (row.scrollLeft - d.scroll0) - (d.sec.offsetLeft - d.left0);
  d.sec.style.transform = `translate(${dx}px, -6px) scale(1.015)`;
}

function StepIcon({ state }) {
  if (state === "done") {
    return svg(14, <path d="M20 6 9 17l-5-5" />, "#28a745", 2.5);
  }
  if (state === "active") {
    return (
      <span
        style={{
          width: "10px",
          height: "10px",
          borderRadius: "50%",
          border: "2px solid #f6c7a8",
          borderTopColor: ORANGE,
          animation: "pine-spin 0.8s linear infinite",
          display: "block",
        }}
      />
    );
  }
  return (
    <span
      style={{
        width: "10px",
        height: "10px",
        borderRadius: "50%",
        border: "1.5px solid #c4c8cc",
        display: "block",
      }}
    />
  );
}

/** One row of the external analysis: tone badge, text, source · date. */
function ExternalRow({ tone, text, meta, href, note }) {
  const [fg, bg] = TONES[tone] || TONES.Neutral;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "80px 1fr",
        gap: "10px",
        alignItems: "start",
      }}
    >
      <span
        style={{
          fontSize: "12px",
          fontWeight: 500,
          textAlign: "center",
          borderRadius: "3px",
          lineHeight: "22px",
          marginTop: "2px",
          color: fg,
          background: bg,
        }}
      >
        {tone}
      </span>
      <div>
        <div style={{ textWrap: "pretty" }}>{text}</div>
        <div style={{ fontSize: "13px", color: MUTED, lineHeight: 1.6 }}>
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: ORANGE }}
            >
              {meta.source}
            </a>
          ) : (
            meta.source
          )}
          {meta.date ? ` · ${meta.date}` : ""}
        </div>
        {note && (
          <div style={{ fontSize: "13px", color: MUTED, lineHeight: 1.6, textWrap: "pretty" }}>
            {note}
          </div>
        )}
      </div>
    </div>
  );
}

export default function SourceAnalysis({
  role_id,
  candidate,
  onClose,
  onFilesChanged,
  onNoteSaved,
}) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { token } = useOutletContext();

  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  // Fills the browser window; every opening starts at the normal size.
  const [full, setFull] = useState(false);
  // Only the panels fill the window; everything in them works as before.
  const [panelsFull, setPanelsFull] = useState(false);
  // The panels scroll sideways under the toolbar's arrows.
  const strip = useRef(null);
  // One panel and the gap; the snap settles it exactly.
  const scrollStrip = (dir) => {
    const row = strip.current;
    if (!row) return;
    const first = row.querySelector("[data-panel]");
    row.scrollBy({
      left: dir * ((first ? first.offsetWidth : 400) + STRIP_GAP),
      behavior: "smooth",
    });
  };

  // Your own notes on this candidate for this role, autosaved. Colleagues'
  // notes are shown read-only beneath them.
  const [general, setGeneral] = useState("");
  const [others, setOthers] = useState([]);
  // null until loaded, false when they could not be read. Until they are
  // loaded nothing is saved, so an empty box never overwrites saved notes.
  const [notesReady, setNotesReady] = useState(null);
  const [noteStatus, setNoteStatus] = useState("");
  const [noteSavedAt, setNoteSavedAt] = useState(null);
  const [qNotes, setQNotes] = useState({});
  const [openQ, setOpenQ] = useState(null);

  // What the server has, the text now, and the save in flight. Saves go one
  // at a time, so an older text can never land after a newer one.
  const canSave = useRef(false);
  const savedText = useRef("");
  const textNow = useRef("");
  textNow.current = general;
  const saving = useRef(false);
  const owed = useRef(false);

  const flushNote = () => {
    const text = textNow.current;
    if (!canSave.current || text === savedText.current) return;
    if (text.length > MAX_NOTE_CHARS) {
      setNoteStatus("too-long");
      return;
    }
    if (saving.current) {
      owed.current = true;
      return;
    }
    saving.current = true;
    setNoteStatus("saving");
    axios
      .post(
        `${API_BASE_URL}/workstation/analysis_note`,
        { role_id, candidate_id: candidate?.candidate_id, body: text },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((res) => {
        savedText.current = text;
        setNoteSavedAt(res.data?.saved_at || null);
        setNoteStatus("saved");
        // The grid's Consultant Notes cell shows this same note.
        if (onNoteSaved) onNoteSaved(text, res.data?.saved_at || null);
      })
      .catch((err) => {
        const status = err.response?.status;
        // App.js is already logging the user out and navigating away.
        if (status === 401 || status === 403) return;
        // The text is still in the box; the next keystroke tries again.
        setNoteStatus("error");
      })
      .finally(() => {
        saving.current = false;
        // Typed while that save was out: send the newer text now.
        if (owed.current) {
          owed.current = false;
          flushRef.current();
        }
      });
  };
  const flushRef = useRef(flushNote);
  flushRef.current = flushNote;

  // The candidate's call transcripts, and which notes tab is showing.
  const [transcripts, setTranscripts] = useState([]);
  const [notesTab, setNotesTab] = useState("mine");

  // Transcribe call audio: the same two calls the dossier makes - save the
  // recording against this candidate and role, then transcribe it, which
  // stores the text and deletes the audio. Audio or video.
  const audioInput = useRef(null);
  const [callStep, setCallStep] = useState("");
  const [callMsg, setCallMsg] = useState(null);

  const transcribeCall = async (file) => {
    if (!file || callStep) return;
    if (file.size > MAX_RECORDING_BYTES) {
      setCallMsg({ ok: false, text: "That file is over 500 MB, so it cannot be uploaded." });
      return;
    }
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    setCallMsg(null);
    setCallStep("uploading");
    try {
      const form = new FormData();
      form.append("candidateId", candidate?.candidate_id);
      form.append("roleId", role_id);
      form.append("recording", file);
      const up = await axios.post(
        `${API_BASE_URL}/candidates/upload_candidate_recording`,
        form,
        auth,
      );

      setCallStep("transcribing");
      const tr = await axios.post(
        `${API_BASE_URL}/chatgpt/whisper_transcribe`,
        { filename: up.data.file, candidate_id: candidate?.candidate_id },
        auth,
      );

      setTranscripts((t) => [
        {
          id: `new-${Date.now()}`,
          savename: file.name,
          date: new Date().toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          }),
          by_user: "you",
          text: String(tr.data?.transcript || ""),
        },
        ...t,
      ]);
      setNotesTab("transcripts");
      setCallMsg({ ok: true, rerun: true, text: "Transcript added." });
      // Bring the notes panel into view, wherever it has been dragged.
      const row = strip.current;
      const panel = row && row.querySelector('[data-panel="notes"]');
      if (panel) row.scrollTo({ left: panel.offsetLeft, behavior: "smooth" });
    } catch (err) {
      const status = err.response?.status;
      // App.js is already logging the user out and navigating away.
      if (status === 401 || status === 403) return;
      setCallMsg({
        ok: false,
        text: err.response?.data?.error || "Could not transcribe the recording.",
      });
    } finally {
      setCallStep("");
      if (audioInput.current) audioInput.current.value = "";
    }
  };

  // Upload CV: the existing document upload, saved as a resume
  // (documenttype 1) - the same row the grid's R shows. The next analysis
  // reads the newest CV, so a Re-run picks it up.
  const cvInput = useRef(null);
  const [cvInfo, setCvInfo] = useState(null);
  const [cvStep, setCvStep] = useState(false);

  const uploadCv = async (file) => {
    if (!file || cvStep) return;
    if (file.size > MAX_CV_BYTES) {
      setCallMsg({ ok: false, text: "That resume is over 5 MB, so it cannot be uploaded." });
      return;
    }
    setCallMsg(null);
    setCvStep(true);
    try {
      const form = new FormData();
      form.append("candidateId", candidate?.candidate_id);
      form.append("roleId", role_id);
      form.append("documentTypeId", 1);
      form.append("resume", file);
      await axios.post(`${API_BASE_URL}/candidates/upload_candidate_file`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCvInfo({ name: file.name, date: "today" });
      // Old Word files are stored, and get their R, but have no text to read.
      const oldWord = /\.doc$/i.test(file.name);
      setCallMsg({
        ok: !oldWord,
        rerun: !oldWord,
        text: oldWord
          ? "Resume added, but an old Word (.doc) file cannot be read for the analysis. Save it as .docx or PDF to use it."
          : "Resume added.",
      });
      // The grid's R comes from the same row; refresh it quietly.
      if (onFilesChanged) onFilesChanged();
    } catch (err) {
      const status = err.response?.status;
      // App.js is already logging the user out and navigating away.
      if (status === 401 || status === 403) return;
      setCallMsg({
        ok: false,
        text: err.response?.data?.error || "Could not upload the resume.",
      });
    } finally {
      setCvStep(false);
      if (cvInput.current) cvInput.current.value = "";
    }
  };

  // Create Dossier from Analysis: the existing dossier, told to also use the
  // call transcripts, notes and answered questions gathered here. Asks for
  // the two things every dossier needs - gender and the role criteria.
  const [dossierOpen, setDossierOpen] = useState(false);
  const [dossierGender, setDossierGender] = useState("");
  const [dossierCriteria, setDossierCriteria] = useState("");
  const [dossierBusy, setDossierBusy] = useState(false);
  const [dossierError, setDossierError] = useState("");

  const openDossier = async () => {
    setDossierError("");
    setDossierOpen(true);
    // The role's key attributes, as the grid's Create Dossier pre-fills them.
    try {
      const res = await axios.get(
        `${API_BASE_URL}/candidates/get_recording_role_criteria/${candidate?.candidate_id}/${role_id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const names = (res.data?.keywords || []).map((k) => k.name).filter(Boolean);
      setDossierCriteria((c) => c || names.join("\n"));
    } catch (err) {
      // Left blank to type in; the dossier then uses generic attribute names.
    }
  };

  const createDossier = async () => {
    if (!dossierGender || dossierBusy) return;
    setDossierBusy(true);
    setDossierError("");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/candidates/generate_dossier`,
        {
          candidate_id: candidate?.candidate_id,
          role_id,
          gender: dossierGender,
          role_criteria: dossierCriteria,
          include_panel_data: true,
        },
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `dossier_${candidate?.candidate_id}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setDossierOpen(false);
      setCallMsg({ ok: true, text: "Dossier created and downloaded." });
      // The grid's D comes from the row the dossier adds.
      if (onFilesChanged) onFilesChanged();
    } catch (err) {
      const status = err.response?.status;
      // App.js is already logging the user out and navigating away.
      if (status === 401 || status === 403) return;
      setDossierError("Could not create the dossier. Please try again.");
    } finally {
      setDossierBusy(false);
    }
  };

  // One second after typing stops.
  useEffect(() => {
    const t = setTimeout(() => flushRef.current(), 1000);
    return () => clearTimeout(t);
  }, [general]);

  // And on closing the window, so the last words are not lost.
  useEffect(() => {
    const flush = flushRef;
    return () => flush.current();
  }, []);

  // Panel order, changed by dragging a panel's title.
  const [order, setOrder] = useState(readOrder);
  const [dragging, setDragging] = useState(null);
  const orderRef = useRef(order);
  orderRef.current = order;
  const drag = useRef(null);
  const raf = useRef(null);
  const slideFrom = useRef(null);

  // After a reorder, each panel slides from where it was to where it now is.
  useLayoutEffect(() => {
    const row = strip.current;
    const from = slideFrom.current;
    if (!row || !from) return;
    slideFrom.current = null;
    row.querySelectorAll("[data-panel]").forEach((n) => {
      const dx = from[n.dataset.panel] - n.getBoundingClientRect().left;
      if (dx && n.animate) {
        n.animate(
          [{ transform: `translateX(${dx}px)` }, { transform: "translateX(0)" }],
          { duration: 520, easing: "cubic-bezier(.22,.61,.36,1)" },
        );
      }
    });
    if (drag.current && drag.current.started) followPointer(drag.current, row);
  }, [order]);

  const startDrag = (id) => (e) => {
    const row = strip.current;
    const sec = e.currentTarget.closest("[data-panel]");
    if (e.button !== 0 || !row || !sec) return;
    e.preventDefault();
    const d = {
      id,
      sec,
      x0: e.clientX,
      x: e.clientX,
      scroll0: row.scrollLeft,
      left0: sec.offsetLeft,
      started: false,
    };

    const tick = () => {
      raf.current = null;
      if (drag.current !== d || !d.started) return;
      // Near either edge the strip scrolls by itself.
      const box = row.getBoundingClientRect();
      if (d.x > box.right - 80) {
        row.scrollLeft += 10;
        raf.current = requestAnimationFrame(tick);
      } else if (d.x < box.left + 80 && row.scrollLeft > 0) {
        row.scrollLeft -= 10;
        raf.current = requestAnimationFrame(tick);
      }
      followPointer(d, row);

      const current = orderRef.current;
      const shown = current.filter((p) => row.querySelector(`[data-panel="${p}"]`));
      const width = sec.offsetWidth;
      const first = Math.min(
        ...shown.map((p) => row.querySelector(`[data-panel="${p}"]`).offsetLeft),
      );
      const centre = d.left0 + width / 2 + (d.x - d.x0) + (row.scrollLeft - d.scroll0);
      const slotNow = shown.indexOf(id);
      const raw = (centre - first - width / 2) / (width + STRIP_GAP);
      // A little stickiness, so a panel resting near a boundary does not flicker.
      if (Math.abs(raw - slotNow) < 0.55) return;
      const slot = Math.max(0, Math.min(shown.length - 1, Math.round(raw)));
      if (slot === slotNow) return;
      const rest = shown.filter((p) => p !== id);
      rest.splice(slot, 0, id);
      const next = [...current.filter((p) => !shown.includes(p)), ...rest];
      slideFrom.current = panelPositions(row, id);
      orderRef.current = next;
      setOrder(next);
    };

    const move = (ev) => {
      d.x = ev.clientX;
      if (!d.started) {
        // A click on the title is not a drag.
        if (Math.abs(d.x - d.x0) < 4) return;
        d.started = true;
        sec.style.transition = "transform .18s cubic-bezier(.2,.8,.2,1)";
        document.body.style.userSelect = "none";
        document.body.style.cursor = "grabbing";
        row.style.scrollSnapType = "none";
        row.style.scrollBehavior = "auto";
        setDragging(id);
        requestAnimationFrame(() => {
          if (drag.current === d) sec.style.transition = "none";
        });
      }
      if (!raf.current) raf.current = requestAnimationFrame(tick);
    };

    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
      if (drag.current === d) drag.current = null;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      if (raf.current) {
        cancelAnimationFrame(raf.current);
        raf.current = null;
      }
      if (!d.started) return;
      // Put back exactly what the strip's own style sets.
      row.style.scrollSnapType = "x mandatory";
      row.style.scrollBehavior = "smooth";
      sec.style.transition = "transform .32s cubic-bezier(.2,.8,.2,1)";
      sec.style.transform = "translate(0px, 0px) scale(1)";
      setTimeout(() => {
        sec.style.transition = SECTION.transition;
        sec.style.transform = "";
        setDragging(null);
      }, 330);
      try {
        localStorage.setItem(ORDER_KEY, JSON.stringify(orderRef.current));
      } catch (err) {
        // Storage can be blocked; the order then lasts until the window closes.
      }
    };

    d.stop = stop;
    drag.current = d;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
  };

  // Closing mid-drag must not leave listeners or a grabbing cursor behind.
  useEffect(() => {
    const live = drag;
    return () => live.current && live.current.stop();
  }, []);

  const panelStyle = (id, extra) => ({
    ...SECTION,
    ...PANEL,
    ...(panelsFull && PANEL_HALF),
    ...extra,
    order: order.indexOf(id),
    zIndex: dragging === id ? 5 : 1,
    boxShadow:
      dragging === id
        ? `0 18px 40px rgba(0,0,0,0.18), 0 4px 10px rgba(0,0,0,0.08), inset 0 0 0 1px ${ORANGE}`
        : "none",
  });
  // The external check is its own request: slower, separately fallible, and
  // never part of the score. It fills its column in when it lands.
  const [ext, setExt] = useState(null);
  const [extError, setExtError] = useState("");
  const timer = useRef(null);

  // Advances on the design's cadence but stops at the last line and waits
  // there. The request really is still running.
  useEffect(() => {
    timer.current = setInterval(() => {
      setStep((s) => (s >= STEPS.length - 1 ? s : s + 1));
    }, STEP_MS);
    return () => clearInterval(timer.current);
  }, []);

  useEffect(() => {
    // Esc leaves the full-screen panels first, then full page, then closes.
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (panelsFull) setPanelsFull(false);
      else if (full) setFull(false);
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, full, panelsFull]);

  // 0 opens on the saved run when there is one; Re-run bumps it.
  const [runId, setRunId] = useState(0);
  // Requests already sent, by run - see the effect below.
  const sent = useRef({});

  useEffect(() => {
    let live = true;
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    const body = { role_id, candidate_id: candidate?.candidate_id };

    // StrictMode opens the window twice in development. Each request is sent
    // once per run, and the second opening waits on the first one's rather
    // than paying for it again.
    const once = (name, send) => {
      const key = `${name}:${runId}`;
      if (!sent.current[key]) sent.current[key] = send();
      return sent.current[key];
    };

    const runAnalysis = () =>
      once("analysis", () =>
        axios.post(`${API_BASE_URL}/workstation/analyse_candidate`, body, auth),
      )
        .then((res) => live && setResult(res.data))
        .catch((err) => {
          if (!live) return;
          const status = err.response?.status;
          // App.js is already logging the user out and navigating away.
          if (status === 401 || status === 403) return;
          setError(
            err.response?.data?.error || "Could not reach PINE to run the analysis.",
          );
        });

    // Fired at the same moment as the analysis, not after it, so the two
    // waits overlap. A failure here blanks only its own block.
    const runExternal = () =>
      once("external", () =>
        axios.post(`${API_BASE_URL}/workstation/external_check`, body, auth),
      )
        .then((res) => live && setExt(res.data))
        .catch((err) => {
          if (!live) return;
          const status = err.response?.status;
          if (status === 401 || status === 403) return;
          setExtError(
            err.response?.data?.error || "The web search could not be run.",
          );
        });

    // Opening shows the saved run; only what was never run is paid for.
    // Re-run skips the lookup.
    once("saved", () =>
      runId === 0
        ? axios
            .get(
              `${API_BASE_URL}/workstation/latest_analysis/${role_id}/${candidate?.candidate_id}`,
              auth,
            )
            .then((res) => res.data || {})
            // Nothing saved could be read: run as if there were none.
            .catch(() => ({}))
        : Promise.resolve({}),
    ).then((saved) => {
      if (!live) return;
      // Notes come with the opening lookup; Re-run leaves them alone.
      if (runId === 0) {
        if (saved.notes) {
          // Set before the text, so loading it does not count as a change.
          savedText.current = saved.notes.mine || "";
          canSave.current = true;
          setGeneral(saved.notes.mine || "");
          setNoteSavedAt(saved.notes.mine_saved_at || null);
          setOthers(saved.notes.others || []);
          setNotesReady(true);
        } else {
          setNotesReady(false);
        }
        if (saved.transcripts) setTranscripts(saved.transcripts);
        setCvInfo(saved.cv || null);
      }
      if (saved.analysis) setResult(saved.analysis);
      else runAnalysis();
      if (saved.external) setExt(saved.external);
      else runExternal();
    });

    return () => {
      live = false;
    };
  }, [API_BASE_URL, token, role_id, candidate?.candidate_id, runId]);

  const reRun = () => {
    // A saved question keeps its draft by row id. Only drafts under unsaved
    // questions, keyed by place in the list, would land under the wrong one.
    const unsaved = Object.entries(qNotes).filter(([k]) => !k.startsWith("q"));
    if (
      unsaved.some(([, n]) => n && n.trim()) &&
      !window.confirm(
        "Re-run the analysis? The notes under the questions are not saved and will be cleared.",
      )
    )
      return;
    setQNotes((n) => Object.fromEntries(Object.entries(n).filter(([k]) => k.startsWith("q"))));
    setOpenQ(null);
    setResult(null);
    setError("");
    setExt(null);
    setExtError("");
    setStep(0);
    setRunId((n) => n + 1);
  };

  // The questions tab showing, the answer being saved, and why one failed.
  const [qTab, setQTab] = useState("open");
  const [savingQ, setSavingQ] = useState(null);
  const [qError, setQError] = useState("");

  /** Put one saved question back in the list, or drop it when deleted. */
  const replaceQuestion = (id, row) =>
    setResult(
      (r) =>
        r && {
          ...r,
          question_rows: (r.question_rows || []).flatMap((q) =>
            q.id !== id ? [q] : row ? [row] : [],
          ),
        },
    );

  const saveAnswer = async (q, key) => {
    const answer = (qNotes[key] || "").trim();
    if (!answer || savingQ) return;
    setSavingQ(key);
    setQError("");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/workstation/analysis_question/answer`,
        { id: q.id, answer },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      replaceQuestion(q.id, res.data.question);
      setQNotes((n) => ({ ...n, [key]: "" }));
      setOpenQ(null);
    } catch (err) {
      setQError(err.response?.data?.error || "Could not save the answer.");
    } finally {
      setSavingQ(null);
    }
  };

  // A few more questions without re-running the analysis.
  const [suggesting, setSuggesting] = useState(false);
  const suggestMore = async () => {
    if (suggesting) return;
    setSuggesting(true);
    setQError("");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/workstation/suggest_questions`,
        { role_id, candidate_id: candidate?.candidate_id },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setResult((r) => r && { ...r, question_rows: res.data.question_rows });
      setQTab("open");
      if (!res.data.added) {
        setQError("No new questions came back. Try again, or answer a few first.");
      } else if (res.data.warnings?.length) {
        setQError(res.data.warnings.join(" "));
      }
    } catch (err) {
      setQError(err.response?.data?.error || "Could not suggest more questions.");
    } finally {
      setSuggesting(false);
    }
  };

  // 1-2 follow-ups on one answered question, shown under it.
  const [followingUp, setFollowingUp] = useState(null);
  const followUp = async (q) => {
    if (followingUp) return;
    setFollowingUp(q.id);
    setQError("");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/workstation/analysis_question/follow_up`,
        { id: q.id },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setResult((r) => r && { ...r, question_rows: res.data.question_rows });
      if (!res.data.added) {
        setQError("No follow-up came back. The answer may already be complete.");
      } else if (res.data.warnings?.length) {
        setQError(res.data.warnings.join(" "));
      }
    } catch (err) {
      setQError(err.response?.data?.error || "Could not suggest a follow-up.");
    } finally {
      setFollowingUp(null);
    }
  };

  const removeQuestion = async (q) => {
    setQError("");
    try {
      await axios.post(
        `${API_BASE_URL}/workstation/analysis_question/delete`,
        { id: q.id },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      replaceQuestion(q.id, null);
    } catch (err) {
      setQError(err.response?.data?.error || "Could not delete the question.");
    }
  };

  const person = result?.candidate || candidate || {};
  const a = result?.analysis;
  // Saved rows when there are any; otherwise the run's own list, unsaved.
  const questions = result?.question_rows || a?.questions || [];
  // An unsaved question has no status, and counts as still to ask.
  // Follow-ups are left out of both tabs: they show under their answer.
  const answeredQs = questions.filter((q) => q.status === "answered" && !q.parent_id);
  const openQs = questions.filter((q) => q.status !== "answered" && !q.parent_id);
  const shownQs = qTab === "answered" ? answeredQs : openQs;
  const followUpsOf = (id) => questions.filter((f) => f.parent_id === id);
  const done = Boolean(a);
  const years = ext?.lookback_years || 3;
  const employerName = person.company || "the current employer";

  /** One line saying what the analysis actually had to work with. */
  const sourceLine = (() => {
    const s = result?.sources;
    if (!s) return "";
    const bits = [];
    if (s.history) bits.push(`${s.history} ${s.history === 1 ? "role" : "roles"}`);
    if (s.education) bits.push("education");
    // Two kinds: comments from status changes, and notes typed in this panel.
    bits.push(
      s.notes ? `${s.notes} status ${s.notes === 1 ? "note" : "notes"}` : "no status notes",
    );
    bits.push(
      s.panel_notes
        ? `${s.panel_notes} search ${s.panel_notes === 1 ? "note" : "notes"}`
        : "no search notes",
    );
    if (s.linkedin) bits.push("LinkedIn");
    bits.push(s.cv ? "resume" : s.cv_unread ? `resume not read (${s.cv_unread})` : "no resume");
    bits.push(s.transcript ? "transcript" : "no transcript");
    bits.push(s.role_brief ? "full role brief" : "key attributes only");
    return bits.join(" · ");
  })();

  /**
   * The rows for each external group, ready to render or copy.
   *
   * An empty group still gets a row - a Neutral "nothing found" - the way the
   * design shows it, because an empty space reads as "not checked".
   */
  const externalGroups = (() => {
    if (!ext || ext.skipped) return [];

    const toRows = (items, isPerson) =>
      items.map((it) => ({
        tone: toneOf(it.sentiment),
        text: it.summary ? `${it.headline}. ${it.summary}` : it.headline,
        meta: {
          source: `${domainOf(it.url)}${it.kind ? ` · ${KIND_LABEL[it.kind] || it.kind}` : ""}`,
          date: it.date,
        },
        href: it.url,
        note: isPerson
          ? `Possible match on: ${it.matched_on}. Check the source before relying on it.`
          : null,
      }));

    const nothing = (what) => ({
      tone: "Neutral",
      text: `No relevant news found for ${what} in the last ${years} years.`,
      meta: { source: `Web search · ${ext.pages ?? 0} sources`, date: "Checked today" },
      href: null,
      note: null,
    });

    const personRows = !ext.person_searched
      ? [
          {
            tone: "Neutral",
            text: ext.person_skipped_reason || "This person was not searched for.",
            meta: { source: "Not searched", date: "" },
            href: null,
            note: null,
          },
        ]
      : ext.person?.length
        ? toRows(ext.person, true)
        : [nothing("this person")];

    const employerRows = ext.employer?.length
      ? toRows(ext.employer, false)
      : [nothing(employerName)];

    return [
      { label: "About the person", rows: personRows },
      { label: `About ${employerName}`, rows: employerRows },
    ];
  })();

  /**
   * The verdict, written by code from what survived the checks. Never the
   * model's own summary - that could cite an item the checks dropped.
   */
  const verdict = (() => {
    if (!ext || ext.skipped) return "";
    const neg = (list) => (list || []).filter((i) => i.sentiment === "negative").length;
    const pos = (list) => (list || []).filter((i) => i.sentiment === "positive").length;

    const personPart = !ext.person_searched
      ? "The candidate was not searched for."
      : neg(ext.person)
        ? `${neg(ext.person)} negative ${neg(ext.person) === 1 ? "item" : "items"} on the candidate - read the sources before relying on ${neg(ext.person) === 1 ? "it" : "them"}.`
        : "No adverse media found on the candidate.";

    const en = neg(ext.employer);
    const ep = pos(ext.employer);
    const employerPart =
      en || ep
        ? `${employerName}: ${[
            en ? `${en} negative` : "",
            ep ? `${ep} positive` : "",
          ]
            .filter(Boolean)
            .join(", ")} ${en + ep === 1 ? "item" : "items"}${en ? " worth asking about" : ""}.`
        : `No negative news found on ${employerName}.`;

    return `${personPart} ${employerPart}`;
  })();

  const plainText = () => {
    if (!a) return "";
    const L = [`AI Sourcing Analysis — ${person.name || ""}`, ""];
    L.push(
      `Sourcing Score: ${a.score}/100${priorityOf(a.recommendation) ? ` · ${priorityOf(a.recommendation)} PRIORITY` : ""} (based on information available${a.basis ? `: ${a.basis}` : ""})`,
      headlineOf(a),
      "",
    );
    const tl = result?.timeline;
    if (tl?.roles?.length) {
      L.push(
        `Career timeline (avg. tenure ${tl.avg_tenure_years} yrs):`,
        ...tl.roles.map(
          (r) =>
            `• ${r.title || "Role not named"} — ${r.company || "?"}, ${r.start_year}${r.current ? "-now" : ""}, ${fmtYears(r.years)}`,
        ),
        "",
      );
    }
    if (a.strengths?.length) {
      L.push("Strengths:", ...a.strengths.map((s) => `• ${s}`), "");
    }
    if (a.weaknesses?.length) {
      L.push("Weaknesses:", ...a.weaknesses.map((w) => `• ${w}`), "");
    }
    if (a.recommendation) L.push(`Recommendation: ${a.recommendation}`, "");
    if (externalGroups.length) {
      L.push("External analysis (web search, unverified):");
      externalGroups.forEach((g) => {
        L.push(g.label);
        g.rows.forEach((r) =>
          L.push(
            `• [${r.tone}] ${r.text} (${r.href || r.meta.source}${r.meta.date ? `, ${r.meta.date}` : ""})`,
          ),
        );
      });
      if (verdict) L.push(`External verdict: ${verdict}`);
      L.push("");
    }
    const anyQ = questions.length > 0;
    if (anyQ) L.push("Questions to ask:");
    QUESTION_STAGES.forEach(({ stage, label }) => {
      const qs = questions.filter((q) => q.stage === stage && !q.parent_id);
      if (!qs.length) return;
      L.push(label);
      const answerLine = (q, pad) => {
        if (!q.answer) return;
        const by = [q.answered_by, q.answered_at].filter(Boolean).join(", ");
        L.push(`${pad}Answer${by ? ` (${by})` : ""}: ${q.answer}`);
      };
      qs.forEach((q, i) => {
        L.push(`• ${q.question}`);
        if (q.tests) L.push(`  Tests: ${q.tests}`);
        answerLine(q, "  ");
        const note = (qNotes[noteKey(q, stage, i)] || "").trim();
        if (note) L.push(`  Notes: ${note}`);
        if (!q.id) return;
        followUpsOf(q.id).forEach((f) => {
          L.push(`  ↳ Follow-up: ${f.question}`);
          answerLine(f, "    ");
        });
      });
    });
    if (general.trim()) L.push("", "Notes:", general.trim());
    others.forEach((n) => {
      const by = [n.by_user, n.saved_at].filter(Boolean).join(", ");
      L.push("", `Notes${by ? ` (${by})` : ""}:`, String(n.body).trim());
    });
    transcripts.forEach((t) => {
      const by = [t.savename, t.date, t.by_user].filter(Boolean).join(", ");
      L.push("", `Call transcript${by ? ` (${by})` : ""}:`, String(t.text).trim());
    });
    return L.join("\n");
  };

  /**
   * One question with its answer, or its answer box. An answered question
   * gets "+ Follow-up", and its follow-ups render the same way beneath it,
   * indented. One level only: a follow-up has no follow-up of its own.
   */
  const questionItem = (q, key) => {
    const note = qNotes[key] || "";
    const editing = openQ === key;
    const followUps = q.id && !q.parent_id ? followUpsOf(q.id) : [];
    return (
      <div key={key} style={{ display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
          <div style={{ flex: 1, minWidth: 0, textWrap: "pretty" }}>
            {q.parent_id ? "↳ " : "• "}
            {q.question}
          </div>
          {q.id && q.status === "open" && (
            <button
              className="pine-sa-x"
              onClick={() => removeQuestion(q)}
              aria-label="Delete question"
              title="Delete question"
              style={{
                flex: "none",
                width: "22px",
                height: "22px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "3px",
                border: "none",
                background: "none",
                color: MUTED,
                cursor: "pointer",
                padding: 0,
              }}
            >
              {svg(
                11,
                <>
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </>,
                undefined,
                2.5,
              )}
            </button>
          )}
        </div>
        {q.tests && (
          <div
            style={{
              fontSize: "13px",
              color: MUTED,
              paddingLeft: "12px",
              lineHeight: 1.6,
            }}
          >
            Tests: {q.tests}
          </div>
        )}
        {q.status === "answered" && !editing && (
          <div
            style={{
              paddingLeft: "12px",
              marginTop: "6px",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <div
              style={{
                fontSize: "13px",
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                color: TEXT,
                borderLeft: `2px solid ${ORANGE}`,
                paddingLeft: "10px",
              }}
            >
              {q.answer}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flexWrap: "wrap",
                paddingLeft: "12px",
                fontSize: "12px",
                color: MUTED,
              }}
            >
              <span>{[q.answered_by, q.answered_at].filter(Boolean).join(" · ")}</span>
              <button
                className="pine-sa-link"
                onClick={() => {
                  setQNotes((n) => ({ ...n, [key]: q.answer || "" }));
                  setOpenQ(key);
                }}
                style={{ ...LINK_BUTTON, fontSize: "12px" }}
              >
                Edit
              </button>
              {q.id && !q.parent_id && (
                <button
                  className="pine-sa-link"
                  onClick={() => followUp(q)}
                  disabled={Boolean(followingUp)}
                  style={{
                    ...LINK_BUTTON,
                    fontSize: "12px",
                    cursor: followingUp ? "default" : "pointer",
                  }}
                >
                  {followingUp === q.id ? "Thinking of a follow-up…" : "+ Follow-up"}
                </button>
              )}
            </div>
          </div>
        )}
        {(q.status !== "answered" || editing) && (
          <div style={{ paddingLeft: "12px", marginTop: "6px" }}>
            {editing ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <textarea
                  className="pine-sa-input"
                  value={note}
                  onChange={(e) => {
                    const text = e.target.value;
                    setQNotes((n) => ({ ...n, [key]: text }));
                  }}
                  // An unsaved question keeps the old note box, which
                  // closes on blur. A saved one waits for Save or Cancel.
                  onBlur={q.id ? undefined : () => setOpenQ(null)}
                  autoFocus
                  rows={3}
                  placeholder={
                    q.id ? "What the candidate said…" : "Candidate's answer, examples, red flags…"
                  }
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    resize: "vertical",
                    fontFamily: "inherit",
                    fontSize: "13px",
                    lineHeight: 1.5,
                    color: TEXT,
                    background: "#fff",
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                    padding: "8px 10px",
                    outline: "none",
                  }}
                />
                {q.id && (
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <button
                      className="pine-sa-copy"
                      onClick={() => saveAnswer(q, key)}
                      disabled={!note.trim() || savingQ === key}
                      style={{
                        height: "28px",
                        padding: "0 12px",
                        border: `1px solid ${ORANGE}`,
                        borderRadius: "4px",
                        background: "#fff3ec",
                        fontFamily: "inherit",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "#c95518",
                        cursor: note.trim() ? "pointer" : "default",
                        opacity: note.trim() ? 1 : 0.5,
                      }}
                    >
                      {savingQ === key ? "Saving…" : "Save answer"}
                    </button>
                    <button
                      className="pine-sa-link"
                      onClick={() => setOpenQ(null)}
                      style={{ ...LINK_BUTTON, color: MUTED }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button className="pine-sa-link" onClick={() => setOpenQ(key)} style={LINK_BUTTON}>
                <IconPencil />
                {note.trim()
                  ? `${q.id ? "Draft" : "Note"}: ${note.length > 60 ? `${note.slice(0, 60)}…` : note}`
                  : q.id
                    ? "Add answer"
                    : "Add note"}
              </button>
            )}
          </div>
        )}
        {followUps.length > 0 && (
          <div
            style={{
              marginTop: "10px",
              marginLeft: "12px",
              paddingLeft: "14px",
              borderLeft: "1px dashed #dee2e6",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            {followUps.map((f) => questionItem(f, noteKey(f)))}
          </div>
        )}
      </div>
    );
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(plainText());
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (err) {
      toastr.error("Could not copy. Select the text and copy it by hand.");
    }
  };

  const title = [person.designation, person.company].filter(Boolean).join(" at ");
  const loading = !done && !error;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 1050,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: full ? 0 : "2vh 2vw",
        boxSizing: "border-box",
        fontFamily: "Roboto, system-ui, sans-serif",
        color: TEXT,
      }}
    >
      <style>{`
        @keyframes pine-spin{to{transform:rotate(360deg)}}
        .pine-sa-copy:hover{border-color:${ORANGE} !important;color:${ORANGE} !important}
        .pine-sa-close:hover{background:#fff3ec !important}
        .pine-sa-handle:hover{color:${ORANGE}}
        .pine-sa-link:hover{color:#c95518 !important}
        .pine-sa-x:hover{color:${ORANGE} !important}
        .pine-sa-input:focus{border-color:${ORANGE} !important;box-shadow:0 0 0 2px #fde2d1}
        .pine-sa-scroll{scrollbar-width:thin;scrollbar-color:#dcdee1 transparent}
        .pine-sa-scroll::-webkit-scrollbar{width:8px;height:8px}
        .pine-sa-scroll::-webkit-scrollbar-track{background:transparent}
        .pine-sa-scroll::-webkit-scrollbar-thumb{background:#dcdee1;border-radius:4px}
        .pine-sa-scroll::-webkit-scrollbar-thumb:hover{background:#c4c8cc}
      `}</style>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: full ? "none" : done ? "1500px" : "680px",
          maxHeight: "100%",
          height: full || done ? "100%" : undefined,
          background: "#fff",
          borderRadius: full ? 0 : "6px",
          boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "16px",
            padding: "24px 30px 14px",
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <IconRobot />
              <h2 style={{ margin: 0, fontSize: "21px", fontWeight: 500, color: TEXT }}>
                AI Sourcing Analysis
              </h2>
            </div>
            <div style={{ fontSize: "14px", color: ORANGE, fontWeight: 500, lineHeight: 1.4 }}>
              {person.name}
              {title ? ` — ${title}` : ""}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: "none" }}>
            <button
              className="pine-sa-copy"
              onClick={() => setFull((f) => !f)}
              aria-label={full ? "Exit full page" : "Full page"}
              title={full ? "Exit full page" : "Full page"}
              style={{ ...HEADER_BUTTON, border: "1px solid #dee2e6" }}
            >
              {svg(
                13,
                <path
                  d={
                    full
                      ? "M8 3v3a2 2 0 0 1-2 2H3M21 8h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3M16 21v-3a2 2 0 0 1 2-2h3"
                      : "M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3"
                  }
                />,
              )}
            </button>
            <button
              className="pine-sa-close"
              onClick={onClose}
              aria-label="Close"
              style={HEADER_BUTTON}
            >
              {svg(
                12,
                <>
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </>,
                undefined,
                2.5,
              )}
            </button>
          </div>
        </header>

        {loading && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "14px",
              padding: "8px 30px 28px",
            }}
          >
            {STEPS.map((label, i) => {
              const state = i < step ? "done" : i === step ? "active" : "pending";
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    fontSize: "13px",
                    color: state === "pending" ? MUTED : TEXT,
                  }}
                >
                  <span
                    style={{
                      width: "14px",
                      height: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: "none",
                    }}
                  >
                    <StepIcon state={state} />
                  </span>
                  <span>{label}</span>
                </div>
              );
            })}
          </div>
        )}

        {error && (
          <div style={{ padding: "8px 30px 28px" }}>
            <div
              style={{
                background: "#fdf3f2",
                border: "1px solid #f3c9c4",
                borderRadius: "4px",
                padding: "16px",
                fontSize: "13px",
                color: "#8a2b20",
              }}
            >
              {error}
            </div>
          </div>
        )}

        {done && (
          <>
            {/* Cards over the panels. The body scrolls when the window is
                short, but the panels never shrink below 480px. */}
            <div
              className="pine-sa-scroll"
              style={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                padding: "6px 30px 0",
                display: "flex",
                flexDirection: "column",
                gap: "18px",
              }}
            >
            {(result.no_poach ||
              result.opted_out ||
              result.consent_pending ||
              result.warnings?.length > 0) && (
              <div
                style={{
                  flex: "none",
                  background: "#fff8ec",
                  border: "1px solid #f2d9ae",
                  borderRadius: "4px",
                  padding: "10px 14px",
                  fontSize: "13px",
                  color: "#7a5312",
                  lineHeight: 1.6,
                }}
              >
                {result.no_poach && (
                  <div>
                    <strong>No-poach agreement.</strong> This person can be read
                    about, but not approached.
                  </div>
                )}
                {result.opted_out && (
                  <div>
                    <strong>Opted out.</strong> This person has asked not to be
                    contacted.
                  </div>
                )}
                {result.consent_pending && (
                  <div>Under GDPR, consent not given yet.</div>
                )}
                {(result.warnings || []).map((w, i) => (
                  <div key={i}>• {w}</div>
                ))}
              </div>
            )}

            <div
              style={{
                flex: "none",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
                gap: "18px",
              }}
            >
              <ScoreCard analysis={a} />
              {result.timeline?.roles?.length > 0 && (
                <TimelineCard timeline={result.timeline} />
              )}
            </div>

            <div
              style={{
                flex: "1 1 0",
                minHeight: "480px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                // The same element pinned over the window, not a copy, so
                // notes being typed and the panel order survive the switch.
                ...(panelsFull && {
                  position: "fixed",
                  inset: 0,
                  zIndex: 10,
                  minHeight: 0,
                  padding: "16px 30px 12px",
                  background: "#fff",
                }),
              }}
            >
              <div
                style={{
                  flex: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: "8px",
                }}
              >
                <span
                  style={{
                    marginRight: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "12px",
                    color: MUTED,
                  }}
                >
                  <IconGrip />
                  Drag a panel title to reorder
                </span>
                {callMsg && (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "12px",
                      color: callMsg.ok ? MUTED : "#d32f2f",
                    }}
                  >
                    {callMsg.text}
                    {callMsg.rerun && (
                      <button
                        className="pine-sa-link"
                        onClick={() => {
                          setCallMsg(null);
                          reRun();
                        }}
                        style={{ ...LINK_BUTTON, fontSize: "12px" }}
                      >
                        Re-run to use it
                      </button>
                    )}
                  </span>
                )}
                <button
                  className="pine-sa-copy"
                  onClick={openDossier}
                  disabled={dossierBusy}
                  title="Uses everything here: resume, call transcripts, notes and answered questions"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    height: "28px",
                    padding: "0 12px",
                    border: `1px solid ${ORANGE}`,
                    borderRadius: "4px",
                    background: "#fff3ec",
                    fontFamily: "inherit",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#c95518",
                    cursor: dossierBusy ? "default" : "pointer",
                    opacity: dossierBusy ? 0.7 : 1,
                  }}
                >
                  {svg(
                    13,
                    <>
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                      <path d="M14 2v6h6" />
                      <path d="M8 13h8" />
                      <path d="M8 17h5" />
                    </>,
                  )}
                  {dossierBusy ? "Creating dossier…" : "Create Dossier from Analysis"}
                </button>
                <input
                  ref={cvInput}
                  type="file"
                  accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
                  onChange={(e) => uploadCv(e.target.files?.[0])}
                  style={{ display: "none" }}
                />
                <button
                  className="pine-sa-copy"
                  onClick={() => cvInput.current && cvInput.current.click()}
                  disabled={cvStep}
                  title={
                    cvInfo
                      ? `Resume on file: ${cvInfo.name}${cvInfo.date ? ` (${cvInfo.date})` : ""}. Upload a newer one to replace it in the analysis.`
                      : "Upload the candidate's resume (PDF or Word) to use it in the analysis"
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    height: "28px",
                    padding: "0 12px",
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                    background: "#fff",
                    fontFamily: "inherit",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: TEXT,
                    cursor: cvStep ? "default" : "pointer",
                    opacity: cvStep ? 0.7 : 1,
                  }}
                >
                  {svg(
                    13,
                    <>
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                      <path d="M14 2v6h6" />
                      <path d="M12 18v-6" />
                      <path d="m9 15 3-3 3 3" />
                    </>,
                  )}
                  {cvStep ? "Uploading…" : cvInfo ? "Replace Resume" : "Upload Resume"}
                </button>
                <input
                  ref={audioInput}
                  type="file"
                  accept="audio/*,video/*"
                  onChange={(e) => transcribeCall(e.target.files?.[0])}
                  style={{ display: "none" }}
                />
                <button
                  className="pine-sa-copy"
                  onClick={() => audioInput.current && audioInput.current.click()}
                  disabled={Boolean(callStep)}
                  title="Upload a call recording (audio or video) to transcribe it"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    height: "28px",
                    padding: "0 12px",
                    marginRight: "8px",
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                    background: "#fff",
                    fontFamily: "inherit",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: TEXT,
                    cursor: callStep ? "default" : "pointer",
                    opacity: callStep ? 0.7 : 1,
                  }}
                >
                  {svg(
                    13,
                    <>
                      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                      <path d="M12 19v3" />
                    </>,
                  )}
                  {callStep === "uploading"
                    ? "Uploading…"
                    : callStep === "transcribing"
                      ? "Transcribing…"
                      : "Transcribe call audio"}
                </button>
                <span style={{ fontSize: "12px", color: MUTED, marginRight: "4px" }}>
                  Scroll for more
                </span>
                {[
                  [-1, "Scroll left", "m15 18-6-6 6-6"],
                  [1, "Scroll right", "m9 18 6-6-6-6"],
                ].map(([dir, label, d]) => (
                  <button
                    key={dir}
                    className="pine-sa-copy"
                    onClick={() => scrollStrip(dir)}
                    aria-label={label}
                    title={label}
                    style={{
                      width: "28px",
                      height: "28px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #dee2e6",
                      borderRadius: "4px",
                      background: "#fff",
                      color: TEXT,
                      padding: 0,
                      cursor: "pointer",
                    }}
                  >
                    {svg(14, <path d={d} />)}
                  </button>
                ))}
                <button
                  className="pine-sa-copy"
                  onClick={() => setPanelsFull((f) => !f)}
                  aria-label={panelsFull ? "Exit full screen panels" : "Full screen panels"}
                  title={panelsFull ? "Exit full screen panels" : "Full screen panels"}
                  style={{
                    width: "28px",
                    height: "28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginLeft: "4px",
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                    background: "#fff",
                    color: TEXT,
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  {svg(
                    13,
                    <path
                      d={
                        panelsFull
                          ? "M8 3v3a2 2 0 0 1-2 2H3M21 8h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3M16 21v-3a2 2 0 0 1 2-2h3"
                          : "M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3"
                      }
                    />,
                  )}
                </button>
              </div>

              {/* Panels sit in the DOM in a fixed order and are placed with
                  CSS order, so a reorder never remounts one mid-typing. */}
              <div
                ref={strip}
                className="pine-sa-scroll"
                style={{
                  position: "relative",
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  gap: `${STRIP_GAP}px`,
                  overflowX: "auto",
                  overflowY: "hidden",
                  scrollSnapType: "x mandatory",
                  scrollBehavior: "smooth",
                  paddingBottom: "8px",
                }}
              >
              <section
                  data-panel="notes"
                  className="pine-sa-scroll"
                  style={panelStyle("notes", {
                    overflowY: "auto",
                    background: "#fff",
                    border: "1px solid #f6c7a8",
                    gap: "10px",
                    lineHeight: 1.6,
                  })}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "10px",
                    }}
                  >
                    <span
                      className="pine-sa-handle"
                      onPointerDown={startDrag("notes")}
                      title="Drag to reorder"
                      style={HANDLE}
                    >
                      <IconGrip />
                      Notes:
                    </span>
                    {notesTab === "mine" && (
                    <span
                      style={{
                        fontSize: "12px",
                        color:
                          notesReady === false || noteStatus === "error" || noteStatus === "too-long"
                            ? "#d32f2f"
                            : MUTED,
                      }}
                    >
                      {notesReady === false
                        ? "Could not load your notes"
                        : noteStatus === "saving"
                          ? "Saving…"
                          : noteStatus === "error"
                            ? "Not saved, retrying as you type"
                            : noteStatus === "too-long"
                              ? "Too long to save (20,000 characters max)"
                              : noteSavedAt
                                ? `Saved · ${noteSavedAt}`
                                : "Saves as you type"}
                    </span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", flex: "none" }}>
                    {[
                      ["mine", "My notes"],
                      ["transcripts", `Call transcript (${transcripts.length})`],
                    ].map(([id, label]) => (
                      <button
                        key={id}
                        className="pine-sa-copy"
                        onClick={() => setNotesTab(id)}
                        style={{
                          height: "28px",
                          padding: "0 12px",
                          border: `1px solid ${notesTab === id ? ORANGE : "#dee2e6"}`,
                          borderRadius: "4px",
                          background: notesTab === id ? "#fff3ec" : "#fff",
                          fontFamily: "inherit",
                          fontSize: "13px",
                          fontWeight: 500,
                          color: notesTab === id ? "#c95518" : TEXT,
                          cursor: "pointer",
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {notesTab === "transcripts" ? (
                    transcripts.length ? (
                      transcripts.map((t) => (
                        <div
                          key={t.id}
                          style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "none" }}
                        >
                          <span style={{ fontSize: "12px", color: MUTED }}>
                            {[t.savename, t.date, t.by_user].filter(Boolean).join(" · ")}
                          </span>
                          <div
                            style={{
                              fontSize: "13px",
                              lineHeight: 1.7,
                              whiteSpace: "pre-wrap",
                              color: TEXT,
                              borderLeft: "2px solid #dee2e6",
                              paddingLeft: "10px",
                            }}
                          >
                            {t.text}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ fontSize: "13px", color: MUTED }}>
                        No call transcript yet. Use "Transcribe call audio" above to add one.
                      </div>
                    )
                  ) : (
                  <>
                  <textarea
                    className="pine-sa-input"
                    value={general}
                    onChange={(e) => setGeneral(e.target.value)}
                    onBlur={() => flushRef.current()}
                    // Unreadable notes are not overwritten with an empty box.
                    readOnly={notesReady === false}
                    placeholder={
                      notesReady === false
                        ? "Your saved notes could not be loaded, so this box is read-only. Close and reopen to try again."
                        : "Notes from the conversation — motivation, notice period, compensation, next steps…"
                    }
                    style={{
                      flex: others.length ? "none" : 1,
                      height: others.length ? "200px" : undefined,
                      minHeight: "160px",
                      width: "100%",
                      boxSizing: "border-box",
                      resize: "none",
                      fontFamily: "inherit",
                      fontSize: "14px",
                      lineHeight: 1.6,
                      color: TEXT,
                      background: "#fff",
                      border: "1px solid #dee2e6",
                      borderRadius: "4px",
                      padding: "10px 12px",
                      outline: "none",
                    }}
                  />
                  <span style={{ fontSize: "12px", color: MUTED }}>
                    Included when you copy
                  </span>
                  {others.map((n, i) => (
                    <div
                      key={i}
                      style={{ display: "flex", flexDirection: "column", gap: "2px", flex: "none" }}
                    >
                      <span style={{ fontSize: "12px", color: MUTED }}>
                        {[n.by_user, n.saved_at].filter(Boolean).join(" · ")}
                      </span>
                      <div
                        style={{
                          fontSize: "13px",
                          lineHeight: 1.6,
                          whiteSpace: "pre-wrap",
                          color: TEXT,
                          borderLeft: "2px solid #dee2e6",
                          paddingLeft: "10px",
                        }}
                      >
                        {n.body}
                      </div>
                    </div>
                  ))}
                  </>
                  )}
                </section>

              <section
                data-panel="strengths"
                className="pine-sa-scroll"
                style={panelStyle("strengths")}
              >
                <div
                  className="pine-sa-handle"
                  onPointerDown={startDrag("strengths")}
                  title="Drag to reorder"
                  style={HANDLE}
                >
                  <IconGrip />
                  Strengths:
                </div>
                {a.strengths?.length > 0 ? (
                  a.strengths.map((s, i) => (
                    <div key={i} style={{ textWrap: "pretty" }}>
                      • {s}
                    </div>
                  ))
                ) : (
                  <div style={{ color: MUTED }}>None were given.</div>
                )}

                {a.weaknesses?.length > 0 && (
                  <>
                    <div style={{ fontWeight: 700, marginTop: "22px" }}>Weaknesses:</div>
                    {a.weaknesses.map((w, i) => (
                      <div key={i} style={{ textWrap: "pretty" }}>
                        • {w}
                      </div>
                    ))}
                  </>
                )}

                {a.recommendation && (
                  <div style={{ marginTop: "22px", textWrap: "pretty" }}>
                    <strong style={{ fontWeight: 700 }}>Recommendation:</strong>{" "}
                    {a.recommendation}
                  </div>
                )}
              </section>

              <section
                data-panel="questions"
                className="pine-sa-scroll"
                style={panelStyle("questions", { gap: "14px" })}
              >
                <div
                  className="pine-sa-handle"
                  onPointerDown={startDrag("questions")}
                  title="Drag to reorder"
                  style={HANDLE}
                >
                  <IconGrip />
                  Questions to ask:
                </div>

                {questions.length > 0 && (
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    {[
                      ["open", "To ask", openQs.length],
                      ["answered", "Answered", answeredQs.length],
                    ].map(([id, label, n]) => (
                      <button
                        key={id}
                        className="pine-sa-copy"
                        onClick={() => {
                          setQTab(id);
                          setOpenQ(null);
                        }}
                        style={{
                          height: "28px",
                          padding: "0 12px",
                          border: `1px solid ${qTab === id ? ORANGE : "#dee2e6"}`,
                          borderRadius: "4px",
                          background: qTab === id ? "#fff3ec" : "#fff",
                          fontFamily: "inherit",
                          fontSize: "13px",
                          fontWeight: 500,
                          color: qTab === id ? "#c95518" : TEXT,
                          cursor: "pointer",
                        }}
                      >
                        {label} ({n})
                      </button>
                    ))}
                  </div>
                )}

                {!result.question_rows && questions.length > 0 && (
                  <div style={{ fontSize: "13px", color: MUTED }}>
                    These questions could not be saved, so answers to them cannot be kept.
                  </div>
                )}
                {qError && (
                  <div style={{ fontSize: "13px", color: "#d32f2f" }}>{qError}</div>
                )}

                {!questions.length && (
                  <div style={{ color: MUTED }}>No questions were produced.</div>
                )}
                {questions.length > 0 && !shownQs.length && (
                  <div style={{ color: MUTED }}>
                    {qTab === "answered"
                      ? "Nothing answered yet. Write an answer under a question in To ask."
                      : "Every question has been answered."}
                  </div>
                )}

                {QUESTION_STAGES.map(({ stage, label }) => {
                  const qs = shownQs.filter((q) => q.stage === stage);
                  if (!qs.length) return null;
                  // Briefing in the panel's orange, assessment in a cool
                  // blue, so the two stages read apart at a glance.
                  const [pfg, pbg] =
                    stage === "briefing" ? ["#c95518", "#fff3ec"] : ["#3d5a80", "#e7eef6"];
                  return (
                    <div
                      key={stage}
                      style={{ display: "flex", flexDirection: "column", gap: "14px" }}
                    >
                      <span
                        style={{
                          alignSelf: "flex-start",
                          fontSize: "12px",
                          fontWeight: 500,
                          borderRadius: "3px",
                          lineHeight: "22px",
                          padding: "0 9px",
                          color: pfg,
                          background: pbg,
                        }}
                      >
                        {label}
                      </span>
                      {qs.map((q, i) => questionItem(q, noteKey(q, stage, i)))}
                    </div>
                  );
                })}
                {qTab === "open" && result.question_rows && (
                  <button
                    className="pine-sa-copy"
                    onClick={suggestMore}
                    disabled={suggesting}
                    style={{
                      alignSelf: "flex-start",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      height: "32px",
                      marginTop: "4px",
                      padding: "0 14px",
                      border: "1px solid #dee2e6",
                      borderRadius: "4px",
                      background: "#fff",
                      fontFamily: "inherit",
                      fontSize: "13px",
                      fontWeight: 500,
                      color: TEXT,
                      cursor: suggesting ? "default" : "pointer",
                      opacity: suggesting ? 0.7 : 1,
                    }}
                  >
                    {suggesting ? "Thinking of more questions…" : "+ Suggest more questions"}
                  </button>
                )}
              </section>

              <section
                data-panel="external"
                className="pine-sa-scroll"
                style={panelStyle("external", { gap: "14px" })}
              >
                <div
                  onPointerDown={startDrag("external")}
                  title="Drag to reorder"
                  style={{ ...HANDLE, fontWeight: 400, gap: "10px", flexWrap: "wrap" }}
                >
                  <span
                    className="pine-sa-handle"
                    style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: 700 }}
                  >
                    <IconGrip />
                    External analysis:
                  </span>
                  <span style={{ fontSize: "13px", color: MUTED }}>
                    {ext && !ext.skipped
                      ? `web search · ${ext.pages ?? 0} sources · last ${years} years`
                      : "web search · not part of the score"}
                  </span>
                </div>

                {!ext && !extError && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      color: MUTED,
                    }}
                  >
                    <span
                      style={{
                        width: "14px",
                        height: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: "none",
                      }}
                    >
                      <StepIcon state="active" />
                    </span>
                    Searching the web for news on {person.name || "this candidate"}
                    {person.company ? ` and ${person.company}` : ""}...
                  </div>
                )}

                {extError && <div style={{ color: "#b02a37" }}>{extError}</div>}

                {ext?.skipped && (
                  <div style={{ color: MUTED, textWrap: "pretty" }}>{ext.skipped}</div>
                )}

                {(ext?.warnings || []).map((w, i) => (
                  <div key={i} style={{ fontSize: "13px", color: "#7a5312" }}>
                    • {w}
                  </div>
                ))}

                {externalGroups.map((g) => (
                  <div
                    key={g.label}
                    style={{ display: "flex", flexDirection: "column", gap: "12px" }}
                  >
                    <div style={{ fontSize: "13px", color: MUTED }}>{g.label}</div>
                    {g.rows.map((r, i) => (
                      <ExternalRow key={i} {...r} />
                    ))}
                  </div>
                ))}

                {verdict && (
                  <div style={{ textWrap: "pretty" }}>
                    <strong style={{ fontWeight: 700 }}>External verdict:</strong>{" "}
                    {verdict}
                  </div>
                )}
              </section>
              </div>
            </div>
            </div>

            {dossierOpen && (() => {
              const notesCount = (general.trim() ? 1 : 0) + others.length;
              const answeredCount = questions.filter((q) => q.status === "answered").length;
              const uses = [
                cvInfo && "the resume",
                transcripts.length &&
                  `${Math.min(transcripts.length, 3)} call ${transcripts.length === 1 ? "transcript" : "transcripts"}`,
                notesCount && `${notesCount} ${notesCount === 1 ? "note" : "notes"}`,
                answeredCount &&
                  `${answeredCount} answered ${answeredCount === 1 ? "question" : "questions"}`,
              ].filter(Boolean);
              const missing = [!cvInfo && "the resume", !transcripts.length && "a call transcript"].filter(Boolean);
              return (
                // Fixed, so it sits above the full-screen panels too.
                <div
                  onClick={() => !dossierBusy && setDossierOpen(false)}
                  style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 30,
                    background: "rgba(0,0,0,0.35)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "16px",
                  }}
                >
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: "100%",
                      maxWidth: "520px",
                      background: "#fff",
                      borderRadius: "6px",
                      boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
                      padding: "22px 24px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "14px",
                      fontSize: "14px",
                      color: TEXT,
                    }}
                  >
                    <div style={{ fontSize: "17px", fontWeight: 500 }}>
                      Create Dossier from Analysis
                    </div>
                    <div style={{ fontSize: "13px", color: MUTED, lineHeight: 1.6 }}>
                      Uses the career history and education
                      {uses.length ? `, plus ${uses.join(", ")}` : ""}.
                    </div>
                    {missing.length > 0 && (
                      <div
                        style={{
                          fontSize: "13px",
                          lineHeight: 1.6,
                          color: "#c95518",
                          background: "#fff3ec",
                          borderRadius: "4px",
                          padding: "8px 12px",
                        }}
                      >
                        For a better dossier, add {missing.join(" and ")} first, with{" "}
                        {[!cvInfo && "Upload Resume", !transcripts.length && "Transcribe call audio"]
                          .filter(Boolean)
                          .join(" and ")}
                        .
                      </div>
                    )}
                    <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      <span style={{ fontWeight: 500 }}>Gender</span>
                      <select
                        className="pine-sa-input"
                        value={dossierGender}
                        onChange={(e) => setDossierGender(e.target.value)}
                        style={{
                          height: "34px",
                          fontFamily: "inherit",
                          fontSize: "14px",
                          border: "1px solid #dee2e6",
                          borderRadius: "4px",
                          padding: "0 8px",
                          outline: "none",
                          background: "#fff",
                          color: TEXT,
                        }}
                      >
                        <option value="">Select gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </label>
                    <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      <span style={{ fontWeight: 500 }}>
                        Role criteria{" "}
                        <span style={{ fontWeight: 400, color: MUTED, fontSize: "12px" }}>
                          one per line, the first three become Role Attributes 1-3
                        </span>
                      </span>
                      <textarea
                        className="pine-sa-input"
                        value={dossierCriteria}
                        onChange={(e) => setDossierCriteria(e.target.value)}
                        rows={4}
                        style={{
                          width: "100%",
                          boxSizing: "border-box",
                          resize: "vertical",
                          fontFamily: "inherit",
                          fontSize: "13px",
                          lineHeight: 1.5,
                          color: TEXT,
                          border: "1px solid #dee2e6",
                          borderRadius: "4px",
                          padding: "8px 10px",
                          outline: "none",
                        }}
                      />
                    </label>
                    {dossierError && (
                      <div style={{ fontSize: "13px", color: "#d32f2f" }}>{dossierError}</div>
                    )}
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                      <button
                        className="pine-sa-copy"
                        onClick={() => setDossierOpen(false)}
                        disabled={dossierBusy}
                        style={{
                          height: "34px",
                          padding: "0 16px",
                          border: "1px solid #dee2e6",
                          borderRadius: "4px",
                          background: "#fff",
                          fontFamily: "inherit",
                          fontSize: "14px",
                          color: TEXT,
                          cursor: "pointer",
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={createDossier}
                        disabled={!dossierGender || dossierBusy}
                        style={{
                          height: "34px",
                          padding: "0 16px",
                          border: "none",
                          borderRadius: "4px",
                          background: ORANGE,
                          fontFamily: "inherit",
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#fff",
                          cursor: !dossierGender || dossierBusy ? "default" : "pointer",
                          opacity: !dossierGender || dossierBusy ? 0.6 : 1,
                        }}
                      >
                        {dossierBusy ? "Creating dossier… about a minute" : "Create dossier"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}

            <footer
              style={{
                display: "flex",
                alignItems: "center",
                gap: "14px",
                padding: "18px 30px 22px",
                flexWrap: "wrap",
              }}
            >
              <button
                className="pine-sa-copy"
                onClick={copy}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  height: "38px",
                  padding: "0 16px",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  background: "#fff",
                  fontFamily: "inherit",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: TEXT,
                  cursor: "pointer",
                }}
              >
                <IconCopy />
                {copied ? "Copied" : "Copy"}
              </button>
              <span style={{ fontSize: "13px", color: MUTED }}>{sourceLine}</span>
              <span style={{ marginLeft: "auto", fontSize: "13px", color: MUTED }}>
                {result.saved_id
                  ? `Analysed ${result.saved_at || "just now"}${result.by_user ? ` by ${result.by_user}` : ""}`
                  : "This run could not be saved"}
              </span>
              <button
                className="pine-sa-copy"
                onClick={reRun}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  height: "38px",
                  padding: "0 16px",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  background: "#fff",
                  fontFamily: "inherit",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: TEXT,
                  cursor: "pointer",
                }}
              >
                {svg(14, <path d="M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5" />)}
                Re-run
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
